import { useState, useRef, useEffect } from 'react';
import { GaEventsProps } from '@/utils/gaTypes';
import { getDataAttributes } from '@/utils/dataAttributes';
import { trackEventWithGaEvents } from '@/utils/gaDataPush';
import Button from '../Button/Button';
import ArrowDown from '../icons/ArrowDown';
import beTwMerge from '../../../utils/beTwMerge';
import { ColorKeys } from '../../../styles/colors';

export type DropdownButtonOptionProps = {
  label: string;
  href?: string;
  target?: string;
  onClick?: () => void;
  className?: string;
  gaEvents?: GaEventsProps[];
};

export function DropdownButtonOption({
  label,
  href,
  target = '_self',
  onClick,
  className = '',
  gaEvents,
}: DropdownButtonOptionProps) {
  return (
    <div
      className={beTwMerge(
        'tw-text-[14px] tw-px-4 tw-py-2 tw-min-h-[44px] tw-leading-7 hover:tw-bg-color-grey-50 tw-text-left',
        className
      )}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={event => {
        if (event.key === 'Enter') {
          onClick && onClick();
        }
      }}
    >
      {href ? (
        <a
          href={href}
          target={target}
          {...getDataAttributes({ gaEvents })}
          onClick={() => {
            gaEvents?.forEach(gaEvent => {
              trackEventWithGaEvents(gaEvent, false);
            });
          }}
          className="tw-block hover:tw-text-color-black hover:tw-no-underline"
        >
          {label}
        </a>
      ) : (
        <div className="tw-cursor-pointer">{label}</div>
      )}
    </div>
  );
}

export type DropdownButtonProps = {
  btnRef?: any;
  label: string;
  color?: ColorKeys;
  type?: 'link' | 'primary' | 'secondary';
  options: DropdownButtonOptionProps[];
  onToggle?: () => void;
  onClick?: () => void;
  className?: string;
  title?: string;
  ariaLabel?: string;
  styles?: any;
};

export default function DropdownButton({
  btnRef,
  label,
  color = 'dark-green',
  options,
  onToggle,
  className = '',
  type = 'primary',
  title,
  ariaLabel,
  styles = {},
}: DropdownButtonProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  const resolvedAriaLabel = ariaLabel || title || label;

  const [initialScrollOffset, setInitialScrollOffset] = useState<number | null>(null);

  const arrowClassNames =
    {
      primary: open
        ? {
            white: `tw-stroke-color-white`,
            'light-mauve': `tw-stroke-color-black`,
            cream: `tw-stroke-color-black`,
          }
        : {
            white: `tw-stroke-color-black group-hover:tw-stroke-color-white`,
            'light-mauve': `tw-stroke-color-black`,
            cream: `tw-stroke-color-black`,
          },
      secondary: open
        ? {
            white: `tw-stroke-color-black`,
            'light-mauve': `tw-stroke-color-black`,
            cream: `tw-stroke-color-black`,
          }
        : {
            'dark-green': 'tw-stroke-color-black group-hover:lg:tw-stroke-color-white',
            white: `tw-stroke-color-white group-hover:tw-stroke-color-black`,
            'light-green': `tw-stroke-color-light-green group-hover:lg:tw-stroke-color-white`,
            'light-mauve': `tw-stroke-color-light-mauve group-hover:tw-stroke-color-black`,
            cream: `tw-stroke-color-cream group-hover:tw-stroke-color-black`,
            'dark-rose': 'tw-stroke-color-dark-rose group-hover:tw-stroke-color-white',
          },
    }[type][color] || 'tw-stroke-color-white';

  const labelWithIcon = (
    <>
      <span className="tw-grow tw-text-center">{label}</span>
      <ArrowDown
        className={beTwMerge(
          `
      tw-ml-2
      tw-origin-center`,
          arrowClassNames,
          open ? 'tw-rotate-180' : ''
        )}
      />
    </>
  );

  const layoutClassNames = 'tw-flex tw-justify-between tw-items-center tw-indent-5 md:tw-indent-0';

  const toggle = () => {
    const urlHash = window.location.hash;

    if (urlHash) {
      setInitialScrollOffset(window.scrollY);
    }

    setOpen(!open);
    onToggle && onToggle();
  };

  const activeClassNames = {
    primary: open
      ? {
          'dark-green': `tw-bg-color-dark-green-light`,
          white: `tw-text-color-white tw-bg-color-black`,
          'light-green': `tw-bg-color-light-green-light`,
          'light-mauve': `tw-bg-color-light-mauve-dark`,
          cream: `tw-bg-color-cream-dark`,
          'dark-rose': `tw-bg-color-dark-rose-dark`,
        }
      : {},
    secondary: open
      ? {
          'dark-green': `tw-text-color-white tw-bg-color-black`,
          white: `tw-text-color-black tw-bg-color-white`,
          'light-green': `tw-text-color-white tw-bg-color-light-green`,
          'light-mauve': `tw-text-color-black tw-bg-color-light-mauve`,
          cream: `tw-text-color-black tw-bg-color-cream`,
          'dark-rose': `tw-text-color-white tw-bg-color-dark-rose`,
        }
      : {},
  }[type][color];

  useEffect(() => {
    const handleClickOutside = event => {
      if (ref.current && !ref.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside, false);
    return () => {
      document.removeEventListener('click', handleClickOutside, false);
    };
  }, []);

  useEffect(() => {
    if (initialScrollOffset !== null) {
      setTimeout(() => {
        window.scrollTo(0, initialScrollOffset);
      }, 0);

      setInitialScrollOffset(null);
    }
  }, [initialScrollOffset]);

  return (
    <div
      className={beTwMerge(
        'tw-inline-block tw-relative tw-w-full md:tw-w-auto tw-min-w-[128px] tw-max-w-[480px]',
        className
      )}
      ref={btnRef}
      style={styles}
    >
      <Button
        ref={ref}
        type={type}
        color={color}
        label={labelWithIcon}
        onClick={() => {
          toggle();
          return false;
        }}
        className={`tw-group ${activeClassNames} ${layoutClassNames}`}
        aria-label={resolvedAriaLabel}
        styles={styles}
      />
      {open && (
        <div className="tw-bg-color-white tw-text-color-black tw-absolute tw-w-full tw-z-10 tw-shadow-[0px_4px_4px_0px_rgba(0,0,0,0.15)]">
          {options.map((option, i) => (
            <DropdownButtonOption key={i} {...option} />
          ))}
        </div>
      )}
    </div>
  );
}
